/**
 * PanelTaskList — the read-only Tasks section rendered inside the Firm /
 * User Detail Panel (Dashboard Slice I — ADR-0089 + ADR-0090).
 *
 * Renders two groups: open tasks ("Open"), and recently-closed tasks
 * ("Recently closed · 7d"). When both are empty, a single empty-state line
 * stands in for the section ("No tasks about this firm."). The empty-state
 * is itself the affordance — the operator opens the panel to see whether
 * there's anything outstanding here, and an empty section is a meaningful
 * "no" rather than absence.
 *
 * Read-only by design. Close-loop actions (mark-done / send-back / clear /
 * set-commit / snooze / unsnooze) still happen from the Dashboard, where the
 * row's expand mechanic + viewer-relative actions live. The panel is broader
 * context — "here's the firm's whole chase queue including parked rows" —
 * not the close surface. Per ADR-0089 §2.
 *
 * Coupled to the canonical wire shape (DashboardRow) so the Dashboard's row
 * contract is the row contract. Adding a field to DashboardRow lights up
 * here automatically without a parallel projection.
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  operatorByEmail,
  type DashboardRow,
  type TaskPriority,
} from "@command-center/shared";

export function PanelTaskList({
  openTasks,
  closedTasks,
  testId,
  emptyLabel = "No tasks about this entity.",
}: {
  openTasks: DashboardRow[];
  closedTasks: DashboardRow[];
  testId: string;
  /** Override the empty-state copy (Firm panel says "this firm", User panel
   *  says "this user"). */
  emptyLabel?: string;
}) {
  const noneOpen = openTasks.length === 0;
  const noneClosed = closedTasks.length === 0;
  if (noneOpen && noneClosed) {
    return (
      <p
        className="text-sm italic text-muted-foreground"
        data-testid={`${testId}-empty`}
      >
        {emptyLabel}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Subsection
        label={`Open (${openTasks.length})`}
        rows={openTasks}
        emptyCopy="None open."
        testId={`${testId}-open`}
        closed={false}
      />
      <Subsection
        label={`Recently closed · 7d (${closedTasks.length})`}
        rows={closedTasks}
        emptyCopy="None closed in the last 7 days."
        testId={`${testId}-closed`}
        closed
      />
    </div>
  );
}

function Subsection({
  label,
  rows,
  emptyCopy,
  testId,
  closed,
}: {
  label: string;
  rows: DashboardRow[];
  emptyCopy: string;
  testId: string;
  closed: boolean;
}) {
  return (
    <div data-testid={testId}>
      <h4 className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </h4>
      {rows.length === 0 ? (
        <p
          className="text-xs italic text-muted-foreground"
          data-testid={`${testId}-empty`}
        >
          {emptyCopy}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <PanelTaskRow key={row.id} row={row} closed={closed} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** One row in the panel's Tasks section. Compact, informational, no actions.
 *  Carries: title, priority pill, status badge (with snoozed-until when
 *  applicable), due / commit pills, assigner→assignee. */
function PanelTaskRow({
  row,
  closed,
}: {
  row: DashboardRow;
  closed: boolean;
}) {
  const assignerOperator = operatorByEmail(row.assigner);
  const assigneeOperator = operatorByEmail(row.assignee);
  const assignerDisplay = assignerOperator?.displayName ?? row.assigner;
  const assigneeDisplay = assigneeOperator?.displayName ?? row.assignee;
  return (
    <li
      className={cn(
        "flex flex-col gap-1 rounded-md border px-2 py-1.5 text-xs",
        closed && "opacity-60",
      )}
      data-testid={`panel-task-${row.id}`}
      data-closed={closed ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="min-w-0 flex-1 truncate font-medium text-foreground"
          data-testid={`panel-task-${row.id}-title`}
        >
          {row.title.trim() !== "" ? (
            row.title
          ) : (
            <span className="italic text-muted-foreground">(no title)</span>
          )}
        </span>
        {row.priority !== "Normal" ? (
          <PanelPriorityPill priority={row.priority} />
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {row.isSnoozed && row.snoozedUntil !== null ? (
          <Badge
            variant="muted"
            data-testid={`panel-task-${row.id}-snoozed-until`}
          >
            Snoozed until {row.snoozedUntil}
          </Badge>
        ) : row.status === "done" ? (
          <Badge variant="emerald">Done — awaiting acknowledge</Badge>
        ) : row.status === "in progress" ? (
          <Badge variant="amber">In progress</Badge>
        ) : null}
        {row.dueBy ? (
          <Badge variant="muted">Due {row.dueBy}</Badge>
        ) : null}
        {row.committedBy ? (
          <Badge variant="muted">Commit {row.committedBy}</Badge>
        ) : null}
      </div>
      <div className="text-[10px] text-muted-foreground">
        {assignerDisplay} → {assigneeDisplay}
      </div>
    </li>
  );
}

function PanelPriorityPill({ priority }: { priority: TaskPriority }) {
  const variant: "red" | "amber" | "muted" =
    priority === "Urgent" ? "red" : priority === "High" ? "amber" : "muted";
  return (
    <Badge variant={variant} data-testid="panel-task-priority">
      {priority}
    </Badge>
  );
}
